import React, { memo, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'

import { StandardModal } from '../../common'
import { Button } from '@salesforce/design-system-react'

import { PAGE_SIZE } from '../../../constants'

import { useDispatch, useSelector } from 'react-redux'

import {
  extractForm,
  getFormDisplay,
  updateForm,
  clearForm,
  applyValidation
} from '../../../helpers/form'

import {
  fetchJobCommentsListById,
  resetHoldJobRequestResult,
  getHoldJobRequestResult,
  requestHoldJob
} from '../../../redux/slices/job'

import jobHoldRequestForm from './jobHoldRequestForm'

import useAlert from '../../../hooks/useAlert'

import { createSuccessAlert, createErrorAlert } from '../../../helpers/alert'
import { getFieldValidator } from '../../../redux/slices/global'

const JobHoldRequestModal = ({ isOpen, modalTitle, jobId, onClose }) => {
  const formRef = useRef(null)
  const [formData, setFormData] = useState(jobHoldRequestForm)
  const dispatch = useDispatch()
  const fireAlert = useAlert()
  const validator = useSelector(getFieldValidator)

  const holdJobResult = useSelector(getHoldJobRequestResult)

  const onInputChangedHandler = useCallback((value, name) => {
    setFormData(prev => updateForm(prev, name, value))
  })

  const onSaveButtonClickedHandler = useCallback(() => {
    const form = formRef.current

    if (form) {
      form.dispatchEvent(new Event('submit'))
    }
  }, [])

  const onSaveAndSendPasswordHandler = useCallback(() => {
    const form = formRef.current

    if (form) {
      const formValid = form.reportValidity()

      if (formValid) {
        const data = extractForm(formData)
        dispatch(requestHoldJob({ jobId: jobId, data }))
        onClose()
        setFormData(prev => clearForm(prev))
      }
    }
  }, [formData])

  const formDisplay = getFormDisplay(formData, onInputChangedHandler)

  useEffect(() => {
    if (holdJobResult.statusCode) {
      if ([200, 201].includes(holdJobResult.statusCode)) {
        fireAlert(
          createSuccessAlert(
            'Your request to hold this job has been succesfully sent.',
            1500
          )
        )
      } else {
        fireAlert(createErrorAlert(holdJobResult.error, 1500))
      }
      dispatch(resetHoldJobRequestResult())
      dispatch(
        fetchJobCommentsListById({ jobId: jobId, page: 1, size: PAGE_SIZE })
      )
    }
  }, [holdJobResult])

  useEffect(() => {
    setFormData(prev => applyValidation(prev, validator?.postComment))
  }, [validator])

  useEffect(() => {
    setFormData(prev => {
      const clearedForm = clearForm(prev)
      return clearedForm
    })
  }, [isOpen])

  return (
    <StandardModal
      hasDatePicker={true}
      isOpen={isOpen}
      modalTitle={modalTitle}
      onClose={onClose}
      footer={
        <div className='slds-grid slds-grid_align-end'>
          <Button
            className='slds-col'
            iconName='save'
            iconCategory='utility'
            variant='brand'
            iconPosition='left'
            onClick={onSaveButtonClickedHandler}
            label='Submit Hold Request'
          />
        </div>
      }
    >
      <div className='slds-p-around_x-small'>
        <form ref={formRef} onSubmit={onSaveAndSendPasswordHandler}>
          {formDisplay}
        </form>
      </div>
    </StandardModal>
  )
}

JobHoldRequestModal.propTypes = {
  isOpen: PropTypes.bool,
  modalTitle: PropTypes.string,
  jobId: PropTypes.string,
  onClose: PropTypes.func
}

export default memo(JobHoldRequestModal)
